import React, { useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import FarmerGroupsService from '~/api/services/farmerGroups'
import Layout from '~/components/Layout'
import notify from '~/components/Notify'
import { useAuth } from '~/routes/components/AuthProvider'
import { StyledMenuItem } from '../../CloudStorage/style'
import dayjs from 'dayjs'
import { handleErrors } from '~/utils/helpers'
import { RouteTypes } from '~/routes/constants'
import { FormGroup, Segment, Tab, TabPane } from 'semantic-ui-react'
import { FormFields } from '~/components/FormFields'
import { CLIMATE_CHECK_SLUG } from '~/Constants'
import LanguageTab from './LanguageTab'
import ClimaCheckService from '~/api/services/climaCheck'
import Periods from './Periods'
import Dates from './Dates'
import FarmerGroupInfo from './FarmerGroupInfo'

const dateFields = [
  {
    key: 'start',
    format: 'DD.MM.YYYY HH:mm'
  },
  {
    key: 'end',
    format: 'DD.MM.YYYY HH:mm'
  },
  {
    key: 'periodFrom',
    format: 'DD.MM.YYYY'
  },
  {
    key: 'periodTo',
    format: 'DD.MM.YYYY'
  }
]

const CreateEditClimaCh: React.FC = () => {
  const { t } = useTranslation()
  const { id } = useParams()
  const { systemData: { languages, logged: { languageId } } } = useAuth()
  const languageLocal = languages.find(language => language.id === languageId)
  const navigate = useNavigate()

  const methods = useForm({
    mode: 'onSubmit'
  })

  const { handleSubmit, control, reset, getValues, setValue, formState } = methods

  const [isBusy, setIsBusy] = useState(false)
  const [activeIndex, setActiveIndex] = useState<string | number>(0)
  const [options, setOptions] = useState(null)

  useEffect(() => {
    fetchClimateCh()
  }, [])

  useEffect(() => {
    if (formState.isSubmitted) {
      if (Object.keys(formState.errors).length > 0) {
        notify(t('Please fill in all required fields'), 'error')
      }
    }

  }, [formState.isSubmitted])

  function getMenuItem(pane, index) {
    const languagesErrors = formState.errors?.languages;
    const paneHasError = index in (languagesErrors || {});

    return <StyledMenuItem
      key={pane}
      className='tab-item'
    >
      <div className={`${paneHasError ? 'error' : ''}`}>{pane}</div>
    </StyledMenuItem>
  }

  async function fetchClimateCh() {
    setIsBusy(true);
    try {
      const responseOptions = await ClimaCheckService.getClimaChOptData()
      const responseFarmersList = await FarmerGroupsService.getFarmerGroupsList(CLIMATE_CHECK_SLUG)
      setOptions({ ...responseOptions.data, farmersList: responseFarmersList.data.map(farmer => ({ value: farmer.id, text: farmer.name })) })
      const { languages } = responseOptions.data
      if (id) {
        const response = await ClimaCheckService.getClimaCheck(id)
        const languagesEdit = response.data.languages
        languages.map((language) => {
          const indexLang = languagesEdit.findIndex(languageEd => languageEd.id === language.id)
          if (indexLang < 0) {
            languagesEdit.push({
              id: language.id,
              title: '',
              description: ''
            })
          } else {
            languagesEdit[indexLang].order = language.order
          }
        })
        languagesEdit.sort((a, b) => a.order - b.order)
        languagesEdit.forEach(lang => delete lang.order)
        response.data.languages = languagesEdit
        dateFields.forEach(dateField => response.data[dateField.key] = dayjs(new Date(response.data[dateField.key])).format(dateField.format))
        reset(response.data)
      } else {
        const languagesNew = []
        languages.map((language) => {
          languagesNew.push({
            id: language.id,
            title: '',
            description: ''
          })
        })
        setValue('languages', languagesNew)
      }
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false);
  }

  function convertDate(date) {
    const [day, month, yearAndTime] = date.split('.')
    const year = yearAndTime?.split(' ')[0]
    return (`${year}-${month}-${day}`)
  }

  function convertDateWithTime(date) {
    const [day, month, yearAndTime] = date.split('.')
    const year = yearAndTime?.split(' ')[0]
    const time = yearAndTime?.split(' ')[1]
    return (`${year}-${month}-${day} ${time}`)
  }

  function removeIfNotBase64(languageArray) {
    languageArray.forEach(language => {
      if (!language.file) {
        language.filename = null
        language.originalFilename = null
      }
    })
    return languageArray
  }

  async function onSubmit(data) {
    setIsBusy(true)
    const payload = {
      ...data,
      periodFrom: convertDate(data.periodFrom.toString()),
      periodTo: convertDate(data.periodTo.toString()),
      dateFrom: convertDateWithTime(data.start),
      dateTo: convertDateWithTime(data.end),
      languages: removeIfNotBase64(data.languages)
    }
    try {
      if (id) {
        await ClimaCheckService.updateClimaCheck(id, payload)
        notify(t('You have successfully updated the data collection for the Climate check emissions calculation'), 'success')
        navigate(RouteTypes.CLIMATE_CHECK)
      } else {
        await ClimaCheckService.createClimaCheck(payload)
        notify(t('You have successfully created the data collection for the Climate check emissions calculation'), 'success')
        navigate(RouteTypes.CLIMATE_CHECK)
      }
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false);
  }

  function renderLanguagesForm() {
    return (
      <Tab
        menu={{ secondary: true, pointing: true }}
        renderActiveOnly={false}
        onTabChange={(_, data) => setActiveIndex(data.activeIndex)}
        panes={options?.languages?.map((language, languageIndex) => {
          const langIndex = getValues('languages').findIndex(langValue => langValue.id === language.id)
          return {
            menuItem: getMenuItem(language.label, langIndex),
            pane: <TabPane
              active={languageIndex === activeIndex as number}
              style={{ border: 'none' }}
              key={language?.id}
            >
              <LanguageTab
                index={langIndex}
                language={language}
                control={control}
                getValues={getValues}
                setValue={setValue}
                languages={getValues('languages')}
              />
            </TabPane>
          };
        })}
      />
    );
  }

  function renderForm() {
    return <>
      <FormGroup widths={'equal'} style={{ alignItems: 'flex-end' }}>
        <FormFields.Select
          control={control}
          name={'type'}
          upward
          rules={{
            required: {
              value: true,
              message: t('Please select type')
            }
          }}
          placeholder={t('Please select type')}
          label={t('Climate check calculation logic')}
          options={options?.types.map(type => ({ text: type.label, value: type.key })) || []}
        />
        <FormFields.Select
          control={control}
          name={'farmerGroups'}
          upward
          rules={{
            required: {
              value: true,
              message: t('Please select farmer groups')
            }
          }}
          multiple
          placeholder={t('Please select farmer group')}
          label={t('Farmer groups')}
          options={options?.farmersList || []}
        />
      </FormGroup>
      <Periods
        languageLocal={languageLocal}
      />
      <FarmerGroupInfo/>
      <Dates
        languageLocal={languageLocal}
      />
    </>
  }

  const title = id ? t('Update Climate check') : t('Creation of data collection')

  return <Layout
    title={<span dangerouslySetInnerHTML={{ __html: title }} />}
    isBusy={isBusy}
    onSubmit={handleSubmit(onSubmit)}
    action={id ? t('Update') : t('Save')}
    segmented
    close={RouteTypes.CLIMATE_CHECK}
    isCo2Loader={true}
  >
    {options && !isBusy && <FormProvider {...methods}>
      <Segment>
        {renderLanguagesForm()}
      </Segment>
      <Segment style={{ marginTop: '3em' }}>
        {renderForm()}
      </Segment>
    </FormProvider>
    }
  </Layout>
}
export default CreateEditClimaCh